import { Key, Trash } from "@phosphor-icons/react";

function ConfiguredView({ saml, onRemove }) {
  return (
    <div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
        <div><div className="label-sm text-zinc-500">Provider</div><div className="text-sm font-medium capitalize mt-1">{saml.provider_name}</div></div>
        <div><div className="label-sm text-zinc-500">Slug</div><div className="font-mono text-sm mt-1">{saml.slug}</div></div>
        <div className="col-span-2"><div className="label-sm text-zinc-500">IdP Entity ID</div><div className="font-mono text-[11px] truncate mt-1">{saml.idp_entity_id}</div></div>
      </div>
      <div className="bg-zinc-50 border border-zinc-200 p-3 mb-3">
        <div className="label-sm text-zinc-500">Tenant SAML login URL — give to your users</div>
        <div className="font-mono text-xs mt-1 break-all" data-testid="saml-login-url">{window.location.origin}/api/auth/saml/{saml.slug}/login</div>
      </div>
      <div className="bg-zinc-50 border border-zinc-200 p-3 mb-3">
        <div className="label-sm text-zinc-500">ACS (Assertion Consumer Service) URL — configure in your IdP</div>
        <div className="font-mono text-xs mt-1 break-all" data-testid="saml-acs-url">{saml.acs_url}</div>
      </div>
      <div className="bg-zinc-50 border border-zinc-200 p-3 mb-4">
        <div className="label-sm text-zinc-500">SP Metadata XML — upload to your IdP</div>
        <a href={saml.metadata_url} target="_blank" rel="noopener noreferrer" className="font-mono text-xs mt-1 inline-flex items-center gap-1 text-[#FF3E00] hover:underline break-all" data-testid="saml-metadata-link">
          {saml.metadata_url} ↗
        </a>
      </div>
      {saml.slo_login_url && (
        <div className="bg-emerald-50 border border-emerald-300 p-3 mb-4">
          <div className="label-sm text-emerald-700">Single Logout (SLO) — ENABLED</div>
          <div className="text-xs mt-1 text-zinc-700">
            SP-initiated logout: <span className="font-mono text-[11px]" data-testid="saml-slo-login-url">{saml.slo_login_url}</span>
          </div>
          <div className="text-xs mt-1 text-zinc-700">
            IdP SLS callback: <span className="font-mono text-[11px]" data-testid="saml-sls-url">{saml.sls_url}</span>
          </div>
        </div>
      )}
      <button onClick={onRemove} className="border border-[#D0021B] text-[#D0021B] text-xs font-semibold px-4 py-2 hover:bg-[#D0021B] hover:text-white transition-colors flex items-center gap-2" data-testid="saml-remove-btn">
        <Trash size={12} /> REMOVE SAML CONFIG
      </button>
    </div>
  );
}

function SamlForm({ form, onChange, onSubmit }) {
  return (
    <form onSubmit={onSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="saml-form">
      <div>
        <label className="label-sm text-zinc-500">Provider</label>
        <select value={form.provider_name} onChange={(e) => onChange({...form, provider_name: e.target.value})} className="w-full border-b border-zinc-300 focus:border-zinc-950 bg-transparent py-2 text-sm outline-none">
          <option value="okta">Okta</option>
          <option value="azure_ad">Azure AD / Entra ID</option>
          <option value="onelogin">OneLogin</option>
          <option value="jumpcloud">JumpCloud</option>
          <option value="auth0">Auth0</option>
          <option value="adfs">ADFS</option>
          <option value="generic">Generic SAML 2.0</option>
        </select>
      </div>
      <div>
        <label className="label-sm text-zinc-500">Email attribute key</label>
        <input value={form.attribute_mapping.email} onChange={(e) => onChange({...form, attribute_mapping: {...form.attribute_mapping, email: e.target.value}})} className="w-full border-b border-zinc-300 focus:border-zinc-950 bg-transparent py-2 text-sm outline-none font-mono" data-testid="saml-email-attr-input" />
      </div>
      <div className="md:col-span-2">
        <label className="label-sm text-zinc-500">IdP Entity ID (Issuer)</label>
        <input required value={form.idp_entity_id} onChange={(e) => onChange({...form, idp_entity_id: e.target.value})} placeholder="http://www.okta.com/exk1abc123" className="w-full border-b border-zinc-300 focus:border-zinc-950 bg-transparent py-2 text-sm outline-none font-mono text-[11px]" data-testid="saml-entity-id-input" />
      </div>
      <div className="md:col-span-2">
        <label className="label-sm text-zinc-500">IdP SSO URL</label>
        <input required value={form.idp_sso_url} onChange={(e) => onChange({...form, idp_sso_url: e.target.value})} placeholder="https://yourorg.okta.com/app/yourapp/exk1abc/sso/saml" className="w-full border-b border-zinc-300 focus:border-zinc-950 bg-transparent py-2 text-sm outline-none font-mono text-[11px]" data-testid="saml-sso-url-input" />
      </div>
      <div className="md:col-span-2">
        <label className="label-sm text-zinc-500">IdP SLO URL <span className="text-zinc-400 font-normal">(optional — enables Single Logout)</span></label>
        <input value={form.idp_slo_url} onChange={(e) => onChange({...form, idp_slo_url: e.target.value})} placeholder="https://yourorg.okta.com/app/yourapp/exk1abc/slo/saml" className="w-full border-b border-zinc-300 focus:border-zinc-950 bg-transparent py-2 text-sm outline-none font-mono text-[11px]" data-testid="saml-slo-url-input" />
      </div>
      <div className="md:col-span-2">
        <label className="label-sm text-zinc-500">IdP X.509 Certificate (PEM)</label>
        <textarea required value={form.idp_x509_cert} onChange={(e) => onChange({...form, idp_x509_cert: e.target.value})} rows={5} placeholder="-----BEGIN CERTIFICATE-----&#10;MIID...&#10;-----END CERTIFICATE-----" className="w-full border border-zinc-300 focus:border-zinc-950 bg-transparent p-2 text-[11px] outline-none font-mono" data-testid="saml-cert-input" />
      </div>
      <div className="md:col-span-2">
        <button type="submit" className="bg-zinc-950 text-white text-xs font-semibold px-5 py-2.5 hover:bg-[#FF3E00] flex items-center gap-2" data-testid="saml-save-btn">
          <Key size={12} /> SAVE SAML CONFIG
        </button>
        <div className="font-mono text-[10px] text-zinc-400 mt-2">
          After saving, configure your IdP ACS URL as: <code>{window.location.origin}/api/auth/saml/{`{slug}`}/acs</code>
        </div>
      </div>
    </form>
  );
}

export default function SamlSection({ saml, form, onFormChange, onSave, onRemove }) {
  return (
    <section className="border border-zinc-950 mt-6" data-testid="saml-section">
      <div className="border-b border-zinc-950 bg-zinc-950 text-white px-4 py-2 font-mono text-[11px] tracking-[0.2em] uppercase flex items-center justify-between">
        <span><Key size={12} className="inline mr-2" /> enterprise.sso (SAML 2.0)</span>
        <span className="text-[10px]">{saml?.configured ? "CONFIGURED" : "NOT CONFIGURED"}</span>
      </div>
      <div className="p-6">
        {saml?.configured
          ? <ConfiguredView saml={saml} onRemove={onRemove} />
          : <SamlForm form={form} onChange={onFormChange} onSubmit={onSave} />}
      </div>
    </section>
  );
}
